import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAdminTheme } from '@shared/providers/admin-theme/AdminThemeContext';
import type { AdminMenuGroup } from '@app-types/admin-theme';

/**
 * Full Admin Menu Configuration for A2 (鍼灸)
 */
const fullAdminMenuGroupsA2: AdminMenuGroup[] = [
  {
    label: '治療院',
    icon: <i className="fa fa-hospital-o" aria-hidden="true" />,
    items: [
      { label: '治療院一覧', path: '/clinics' },
      { label: '治療院情報CSV', path: '/clinic_informations' },
      { label: '治療院ユーザーCSV', path: '/clinic_users' },
      { label: '施術者CSV', path: '/massagers' },
    ],
  },
  {
    label: '保険者',
    icon: <i className="fa fa-clipboard" aria-hidden="true" />,
    items: [
      { label: '保険者一覧', path: '/insurers' },
      { label: '県別注意事項', path: '/insurer_attentions' },
    ],
  },
  {
    label: 'レセプト',
    icon: <i className="fa fa-file-text-o" aria-hidden="true" />,
    items: [{ label: 'レセプトCSV', path: '/rezept_histories' }],
  },
  {
    label: '用紙',
    icon: <i className="fa fa-file-text-o" aria-hidden="true" />,
    items: [
      { label: '用紙一覧', path: '/rezept_sheets' },
      { label: '用紙受注状況', path: '/order_rezept_sheets' },
    ],
  },
  {
    label: 'システム',
    icon: <i className="fa fa-cog" aria-hidden="true" />,
    items: [
      { label: '提出期限', path: '/rezept_limits' },
      { label: '休日', path: '/holidays' },
      { label: 'お知らせ', path: '/informations' },
      { label: '管理者', path: '/admins' },
      { label: '郵便番号', path: '/towns' },
      { label: '距離キャッシュ', path: '/distance_caches' },
      { label: '緯度経度キャッシュ', path: '/latlon_caches' },
      { label: '削除治療院', path: '/delete_clinics' },
    ],
  },
  {
    label: 'Design Template',
    icon: <i className="fa fa-palette" aria-hidden="true" />,
    path: '/design-template',
  },
];

/**
 * Site Admin Menu Configuration for A2 (鍼灸)
 */
const siteAdminMenuGroupsA2: AdminMenuGroup[] = [
  {
    label: '治療院',
    icon: <i className="fa fa-hospital-o" aria-hidden="true" />,
    items: [
      { label: '治療院一覧', path: '/clinics' },
      { label: '治療院情報CSV', path: '/clinic_informations' },
      { label: '治療院ユーザーCSV', path: '/clinic_users' },
      { label: '施術者CSV', path: '/massagers' },
    ],
  },
  {
    label: '保険者',
    icon: <i className="fa fa-shield" aria-hidden="true" />,
    items: [
      { label: '保険者一覧', path: '/insurers' },
      { label: '特殊医療助成金', path: '/special_calculation_costs' },
      { label: '県別注意事項', path: '/insurer_attentions' },
    ],
  },
  {
    label: 'レセプト',
    icon: <i className="fa fa-file-text-o" aria-hidden="true" />,
    items: [{ label: 'レセプトCSV', path: '/rezept_histories' }],
  },
  {
    label: '用紙',
    icon: <i className="fa fa-file-text-o" aria-hidden="true" />,
    items: [
      { label: '用紙一覧', path: '/rezept_sheets' },
      { label: '用紙受注状況', path: '/order_rezept_sheets' },
    ],
  },
  {
    label: 'システム',
    icon: <i className="fa fa-cog" aria-hidden="true" />,
    items: [
      { label: '提出期限', path: '/rezept_limits' },
      { label: '休日', path: '/holidays' },
      { label: 'お知らせ', path: '/informations' },
      { label: '管理者', path: '/admins' },
      { label: '郵便番号', path: '/towns' },
      { label: '距離キャッシュ', path: '/distance_caches' },
      { label: '緯度経度キャッシュ', path: '/latlon_caches' },
      { label: '削除治療院', path: '/delete_clinics' },
    ],
  },
  {
    label: 'Design Template',
    icon: <i className="fa fa-palette" aria-hidden="true" />,
    path: '/design-template',
  },
];

/**
 * Get menu groups based on admin type (single menu, no service type)
 */
function getMenuGroups(adminType: 'full' | 'site'): AdminMenuGroup[] {
  return adminType === 'full' ? fullAdminMenuGroupsA2 : siteAdminMenuGroupsA2;
}

const normalizePath = (p: string) => p.replace(/\/$/, '') || '/';

const isPathActive = (currentPath: string, menuPath: string) => {
  const normalizedMenuPath = normalizePath(menuPath);
  return currentPath === normalizedMenuPath || currentPath.startsWith(`${normalizedMenuPath}/`);
};

/**
 * Menu Group Component
 */
const MenuGroupComponent: React.FC<{
  group: AdminMenuGroup;
  basePath: string;
  isExpanded: boolean;
  onToggle: () => void;
  colors: {
    menuOpen: string;
    menuHover: string;
    menuActive: string;
    sidebarText: string;
  };
}> = ({ group, basePath, isExpanded, onToggle, colors }) => {
  const location = useLocation();
  const currentPath = normalizePath(location.pathname);

  // Flat menu link (no group): single link with icon + label
  if (group.path) {
    const fullPath = `${basePath}${group.path}`;
    const isActive = isPathActive(currentPath, fullPath);
    return (
      <div className="border-b border-white/20">
        <Link
          to={fullPath}
          className="flex items-center w-full gap-2 px-4 py-3 transition-colors"
          style={{
            color: colors.sidebarText,
            backgroundColor: isActive ? colors.menuActive : undefined,
          }}
          onMouseEnter={e => {
            if (!isActive) {
              e.currentTarget.style.backgroundColor = colors.menuHover;
            }
          }}
          onMouseLeave={e => {
            if (!isActive) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          <span>{group.icon}</span>
          <span className="font-medium">{group.label}</span>
        </Link>
      </div>
    );
  }

  // Accordion group with items
  return (
    <div className="border-b border-white/20">
      <button
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="flex items-center justify-between w-full px-4 py-3 text-left transition-colors"
        style={{
          color: colors.sidebarText,
          backgroundColor: isExpanded ? colors.menuActive : 'transparent',
        }}
      >
        <span className="flex items-center gap-2">
          <span>{group.icon}</span>
          <span className="font-medium">{group.label}</span>
        </span>
        <span
          className={`inline-flex transform items-center transition-transform duration-200 ease-in-out ${isExpanded ? 'rotate-180' : ''}`}
        >
          <i className="text-lg fa fa-angle-down" aria-hidden="true" />
        </span>
      </button>
      {group.items && (
        <div
          aria-hidden={!isExpanded}
          className={`grid transition-[grid-template-rows,opacity] duration-200 ease-in-out ${
            isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <div
            className={`min-h-0 overflow-hidden ${isExpanded ? '' : 'pointer-events-none'}`}
            style={{ backgroundColor: colors.menuOpen }}
          >
            {group.items.map((item, index) => {
              const fullPath = `${basePath}${item.path}`;
              const isActive = isPathActive(currentPath, fullPath);
              return (
                <Link
                  key={index}
                  to={fullPath}
                  tabIndex={isExpanded ? undefined : -1}
                  className="block px-8 py-2 text-sm transition-colors"
                  style={{
                    color: colors.sidebarText,
                    backgroundColor: isActive ? colors.menuActive : undefined,
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = colors.menuHover;
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Admin Sidebar Props
 */
interface AdminSidebarProps {
  isOpen: boolean;
  onClose?: () => void;
}

/**
 * Admin Sidebar Component
 */
const AdminSidebar: React.FC<AdminSidebarProps> = ({ isOpen, onClose }) => {
  const { adminType, siteType, theme, colors } = useAdminTheme();
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  // Get base path for links
  const basePath = adminType === 'full' ? '/admin' : `/${siteType}/admin`;

  const menuGroups = getMenuGroups(adminType);

  const currentPath = normalizePath(location.pathname);
  const adminRootPath = normalizePath(basePath);
  const isAdminRootPath = currentPath === adminRootPath || currentPath === `${adminRootPath}/home`;
  const activeGroupIndex = menuGroups.findIndex(group => {
    if (group.path) {
      return isPathActive(currentPath, `${basePath}${group.path}`);
    }
    return group.items?.some(item => {
      return isPathActive(currentPath, `${basePath}${item.path}`);
    });
  });
  const autoExpandGroupIndex = activeGroupIndex >= 0 ? activeGroupIndex : isAdminRootPath ? 0 : -1;

  useEffect(() => {
    if (autoExpandGroupIndex < 0 || autoExpandGroupIndex >= menuGroups.length) return;
    setExpandedGroups(prev => {
      if (prev.has(autoExpandGroupIndex)) return prev;
      const next = new Set(prev);
      next.add(autoExpandGroupIndex);
      return next;
    });
  }, [autoExpandGroupIndex, menuGroups.length]);

  const toggleGroup = (index: number) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden" onClick={onClose} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] ${theme.sidebarWidth} shadow-lg z-50 transform transition-transform duration-300 flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: colors.sidebarBg }}
      >
        {/* Logo - Fixed at top */}
        {/* <div className="flex-shrink-0 px-4 py-3 border-b border-white/20 h-[70px] flex items-center">
          <Link to={basePath}>
            <img
              src={theme.logoPath}
              alt="Admin Logo"
              className="max-h-[50px] max-w-full object-contain"
              onError={e => {
                // Fallback if logo doesn't exist
                e.currentTarget.style.display = 'none';
              }}
            />
          </Link>
        </div> */}

        {/* Menu Groups - Scrollable area */}
        <nav className="app-sidebar-scrollbar flex-1 overflow-y-auto py-2">
          {menuGroups.map((group, index) => (
            <MenuGroupComponent
              key={index}
              group={group}
              basePath={basePath}
              isExpanded={expandedGroups.has(index)}
              onToggle={() => toggleGroup(index)}
              colors={{
                menuOpen: colors.menuOpen,
                menuHover: colors.menuHover,
                menuActive: colors.menuActive,
                sidebarText: colors.sidebarText,
              }}
            />
          ))}
        </nav>
      </aside>
    </>
  );
};

export default AdminSidebar;
